import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { Window } from "../../../contexts";
import { Button, Icon, IconButton, Input, Segmented, Select } from "../../../components/ui";
import { GalleryUpload } from "../../../components/inputs";
import { productFields } from "../AddProduct/productFormConfig";
import { useCatalogAutoApprove } from "../../../hooks/useCatalogAutoApprove";

import "./quick-add-product.scss";

const NEW = "__new__";

const MODES = [
  { key: "product", label: "Product" },
  { key: "model", label: "Model" },
  { key: "brand", label: "Brand" },
];

const watchesCategoryId = () =>
  (Window.CATEGORIES || []).find((c) => c.url_name === "watches")?.id;

/**
 * Inline catalogue creation for the product-search surfaces.
 *
 * Three modes, because a rep does not always want a whole product: they may
 * just need the BRAND to exist, or a MODEL under it, and asking for a
 * reference number in those cases was wrong. Only product mode requires a
 * reference; the backend enforces the same per-mode rules.
 *
 * Product mode collects brand + model + reference by default and hides the
 * rest behind "Add details and images", which renders the SAME attribute set
 * as the Settings product form. Those stay optional here on purpose: the point
 * of the inline path is that a rep mid-flow with a client can finish in
 * seconds and complete the record later under My Products.
 *
 * Renders nothing unless the platform auto-approve gate is ON: with it off a
 * new row would be pending and unselectable, so the affordance would be a dead
 * end. The endpoint enforces that rule server-side too.
 */
export function QuickAddProduct({ prefill = {}, onCreated, variant = "prominent" }) {
  const autoApprove = useCatalogAutoApprove();
  const categoryId = watchesCategoryId();

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("product");
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [brandName, setBrandName] = useState("");
  const [modelId, setModelId] = useState("");
  const [modelName, setModelName] = useState("");
  const [reference, setReference] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [values, setValues] = useState({});
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadBrands = () => {
    if (!categoryId) return;
    axios.get(`/api/categories/${categoryId}/brands?all=true`)
      .then((res) => setBrands(res.data || []))
      .catch(() => setBrands([]));
  };

  useEffect(() => {
    if (!open) return;
    setBrandId(prefill.brand_id ? String(prefill.brand_id) : "");
    setReference(prefill.reference_number || prefill.query || "");
    setError("");
    setNotice("");
    loadBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !categoryId || !brandId || brandId === NEW) { setModels([]); return; }
    axios.get(`/api/categories/${categoryId}/brands/${brandId}/models`)
      .then((res) => setModels(res.data || []))
      .catch(() => setModels([]));
  }, [open, categoryId, brandId]);

  const brandIsNew = brandId === NEW;
  const modelIsNew = brandIsNew || modelId === NEW;

  const canSubmit = useMemo(() => {
    if (mode === "brand") return !!brandName.trim();
    if (mode === "model") {
      if (!modelName.trim()) return false;
      return brandIsNew ? !!brandName.trim() : !!brandId;
    }
    if (!reference.trim()) return false;
    if (brandIsNew ? !brandName.trim() : !brandId) return false;
    if (modelIsNew ? !modelName.trim() : !modelId) return false;
    return true;
  }, [mode, brandName, modelName, brandIsNew, brandId, modelIsNew, modelId, reference]);

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const payload = { create: mode, category_id: categoryId, for_store: true };
      if (brandIsNew || mode === "brand") payload.brand_name = brandName.trim();
      else if (brandId) payload.brand_id = Number(brandId);

      if (mode !== "brand") {
        if (modelIsNew) payload.model_name = modelName.trim();
        else if (modelId) payload.model_id = Number(modelId);
      }

      if (mode === "product") {
        payload.reference_number = reference.trim();
        const filled = Object.fromEntries(
          Object.entries(values).filter(([, v]) => v !== "" && v != null)
        );
        if (Object.keys(filled).length) payload.values = filled;
        if (images.length) payload.images = images;
      }

      const res = await axios.post("/api/store-catalog/quick-product", payload);
      const created = res.data;

      if (mode === "product") {
        setOpen(false);
        onCreated?.(created);
        return;
      }

      // Brand / model: nothing to select, so stay open and let the rep carry
      // on - adding a brand is usually the first step of adding a model.
      loadBrands();
      if (mode === "brand") {
        setBrandId(String(created.id));
        setBrandName("");
        setMode("model");
        setNotice("Brand added. Add a model under it, or switch to Product.");
      } else {
        setModelId(String(created.id));
        setModelName("");
        setMode("product");
        setNotice("Model added. Add the reference to create the product.");
      }
    } catch (e) {
      const body = e?.response?.data;
      setError(
        body?.message
          || Object.values(body?.errors || {}).flat().join(" ")
          || "Could not add this."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!autoApprove || !categoryId) return null;

  if (!open) {
    // Prominent inside an empty-results state, quiet (ghost) under a result list.
    return (
      <Button
        variant={variant === "prominent" ? "secondary" : "ghost"}
        size="sm"
        className="qap__trigger"
        data-btn="catalogue-new"
        leadingIcon={<Icon name="Plus" size="sm" />}
        onClick={() => setOpen(true)}
      >
        Add to the catalogue
      </Button>
    );
  }

  const needsModel = mode !== "brand";
  const needsReference = mode === "product";

  return (
    <div className="qap">
      <div className="qap__head">
        <span className="qap__title">Add to the catalogue</span>
        <IconButton variant="ghost" size="sm" label="Cancel" data-btn="catalogue-cancel" onClick={() => setOpen(false)}>
          <Icon name="X" size="sm" />
        </IconButton>
      </div>

      <Segmented
        className="qap__modes"
        size="sm"
        label="What to add"
        options={MODES.map((m) => ({ value: m.key, label: m.label }))}
        value={mode}
        onChange={(key) => { setMode(key); setError(""); setNotice(""); }}
      />

      {error && <div className="qap__error">{error}</div>}
      {notice && <div className="qap__notice">{notice}</div>}

      <div className="qap__row">
        <label className="qap__label" htmlFor="qap-brand">Brand</label>
        {mode === "brand" ? (
          <Input
            size="md"
            placeholder="New brand name"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
          />
        ) : (
          <>
            <Select
              id="qap-brand"
              fullWidth
              label="Brand"
              placeholder="Select brand..."
              clearable
              options={[
                { value: NEW, label: "+ New brand..." },
                ...brands.map((b) => ({ value: String(b.id), label: b.name })),
              ]}
              value={brandId}
              onChange={(v) => { setBrandId(v ?? ""); setModelId(""); setModelName(""); }}
            />
            {brandIsNew && (
              <Input
                size="md"
                placeholder="New brand name"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
              />
            )}
          </>
        )}
      </div>

      {needsModel && (
        <div className="qap__row">
          <label className="qap__label" htmlFor="qap-model">Model</label>
          {mode === "model" || brandIsNew ? (
            <Input
              size="md"
              placeholder="New model name"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
            />
          ) : (
            <>
              <Select
                id="qap-model"
                fullWidth
                label="Model"
                placeholder={brandId ? "Select model..." : "Pick a brand first"}
                clearable
                disabled={!brandId}
                options={[
                  { value: NEW, label: "+ New model..." },
                  ...models.map((m) => ({ value: String(m.id), label: m.name })),
                ]}
                value={modelId}
                onChange={(v) => setModelId(v ?? "")}
              />
              {modelId === NEW && (
                <Input
                  size="md"
                  placeholder="New model name"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                />
              )}
            </>
          )}
        </div>
      )}

      {needsReference && (
        <div className="qap__row">
          <label className="qap__label" htmlFor="qap-ref">Reference number</label>
          <Input
            size="md"
            id="qap-ref"
            placeholder="e.g. 126710BLNR"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </div>
      )}

      {needsReference && (
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={showDetails}
            data-btn="catalogue-details-toggle"
            onClick={() => setShowDetails((v) => !v)}
          >
            {showDetails ? "Hide details and images" : "Add details and images"}
          </Button>

          {showDetails && (
            <div className="qap__details">
              {productFields.map((f) => (
                <div className="qap__row" key={f.name}>
                  <label className="qap__label" htmlFor={`qap-${f.name}`}>{f.label}</label>
                  {/* Free-text fields (Vintage Details) get a real textarea:
                      a single-line input for a sentence about a vintage piece
                      hides most of what was typed while typing it. */}
                  {f.type === "textarea" ? (
                    <textarea
                      className="qap__textarea"
                      id={`qap-${f.name}`}
                      rows={3}
                      value={values[f.name] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                    />
                  ) : (
                    <Input
                      size="md"
                      id={`qap-${f.name}`}
                      type={f.type === "date" ? "date" : "text"}
                      value={values[f.name] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                    />
                  )}
                </div>
              ))}

              <div className="qap__row">
                <label className="qap__label">Images</label>
                <div className="field-group field-group-galleryUpload mt-0">
                  <GalleryUpload value={images} onChange={setImages} />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="qap__actions">
        <Button size="md" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
        <Button size="md" variant="primary" onClick={submit} disabled={!canSubmit || busy}>
          {busy ? "Adding..." : mode === "product" ? "Add and select" : "Add " + mode}
        </Button>
      </div>
    </div>
  );
}

export default QuickAddProduct;
